/**
 * BathroomPreview — a live top-down SVG illustration of the user's bathroom
 * that updates as they fill out the calculator. Floor plan style.
 *
 * Inputs (props):
 *   size_sqft, remodel_type, finish_level, material, fixtures (array of ids)
 *
 * The preview is intentionally stylized (blueprint feel) rather than a photo —
 * a single SVG that adapts to every combination keeps load instant and lets
 * fixtures slide in as the user picks them.
 */

const FINISH_PALETTE = {
  budget: {
    floor: "#EDEAE2",
    wall: "#F1EFEA",
    grout: "#D8D4C9",
    fixture: "#FFFFFF",
    fixtureStroke: "#C5C0B2",
    accent: "#9A9D95",
  },
  mid: {
    floor: "#E5D8BD",
    wall: "#F4ECDA",
    grout: "#C9B98E",
    fixture: "#FFFFFF",
    fixtureStroke: "#B5AC92",
    accent: "#7E8C73",
  },
  premium: {
    floor: "#C9BFAA",
    wall: "#E6ECE7",
    grout: "#9FA697",
    fixture: "#FBFAF7",
    fixtureStroke: "#7E8C73",
    accent: "#3A5340",
  },
  luxury: {
    floor: "#A89178",
    wall: "#E8D5B7",
    grout: "#6B5A45",
    fixture: "#FBFAF7",
    fixtureStroke: "#3A5340",
    accent: "#B85C3F",
  },
};

const MATERIAL_LABEL = {
  ceramic: "Ceramic",
  porcelain: "Porcelain",
  natural_stone: "Natural stone",
  marble: "Marble",
};

const TYPE_LABEL = {
  cosmetic: "Cosmetic refresh",
  partial: "Partial remodel",
  full_gut: "Full gut",
};

export default function BathroomPreview({
  size_sqft = 40,
  remodel_type = "partial",
  finish_level = "mid",
  material = "ceramic",
  fixtures = [],
}) {
  // Bathroom dimensions: assume ~1.6:1 ratio for typical bath.
  // Compute a viewBox that scales with size_sqft while keeping the SVG bounded.
  const sqft = Math.max(15, Math.min(200, Number(size_sqft) || 40));
  // Map sqft (15–200) → display width (200–540) inside a 600×340 viewBox.
  const displayW = 200 + ((sqft - 15) / (200 - 15)) * 340;
  const displayH = displayW * 0.62;
  const cx = 300;
  const cy = 175;
  const x = cx - displayW / 2;
  const y = cy - displayH / 2;

  const palette = FINISH_PALETTE[finish_level] || FINISH_PALETTE.mid;

  // Approx room dimensions in feet for the label (square root + ratio).
  const widthFt = Math.round(Math.sqrt(sqft * 1.6));
  const heightFt = Math.round(sqft / Math.max(widthFt, 1));

  const has = (id) => fixtures.includes(id);

  return (
    <div className="relative" data-testid="bathroom-preview">
      <svg
        viewBox="0 0 600 380"
        className="w-full h-auto"
        style={{ filter: "drop-shadow(0 6px 20px rgba(28,30,27,0.08))" }}
        role="img"
        aria-label="Bathroom floor plan preview"
      >
        {/* Background paper */}
        <rect width="600" height="380" fill="#F9F8F6" rx="8" />

        {/* Blueprint grid */}
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path
              d="M 20 0 L 0 0 0 20"
              fill="none"
              stroke="#E5E3DC"
              strokeWidth="0.5"
            />
          </pattern>
          <pattern
            id="tile-porcelain"
            width="22"
            height="22"
            patternUnits="userSpaceOnUse"
          >
            <rect width="22" height="22" fill={palette.floor} />
            <path
              d="M 22 0 L 0 0 0 22"
              stroke={palette.grout}
              strokeWidth="0.7"
              fill="none"
            />
          </pattern>
          <pattern
            id="tile-marble"
            width="60"
            height="60"
            patternUnits="userSpaceOnUse"
          >
            <rect width="60" height="60" fill={palette.floor} />
            <path
              d="M 0 18 Q 20 6, 40 22 T 60 14"
              stroke={palette.grout}
              strokeWidth="0.9"
              fill="none"
              opacity="0.55"
            />
            <path
              d="M 0 42 Q 22 50, 38 36 T 60 44"
              stroke={palette.grout}
              strokeWidth="0.7"
              fill="none"
              opacity="0.4"
            />
          </pattern>
          <pattern
            id="tile-stone"
            width="14"
            height="14"
            patternUnits="userSpaceOnUse"
          >
            <rect width="14" height="14" fill={palette.floor} />
            <circle cx="3" cy="4" r="0.7" fill={palette.grout} opacity="0.7" />
            <circle cx="10" cy="9" r="0.5" fill={palette.grout} opacity="0.5" />
            <circle cx="6" cy="11" r="0.6" fill={palette.grout} opacity="0.6" />
          </pattern>
          <pattern
            id="heated-floor"
            width="14"
            height="14"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 0 7 Q 3.5 0, 7 7 T 14 7"
              stroke={palette.accent}
              strokeWidth="0.8"
              fill="none"
              opacity="0.45"
            />
          </pattern>
        </defs>

        <rect width="600" height="380" fill="url(#grid)" opacity="0.5" />

        {/* Room outline + floor */}
        <g>
          <rect
            x={x}
            y={y}
            width={displayW}
            height={displayH}
            fill={
              material === "porcelain"
                ? "url(#tile-porcelain)"
                : material === "natural_stone"
                  ? "url(#tile-stone)"
                  : material === "marble"
                    ? "url(#tile-marble)"
                    : palette.floor
            }
            stroke={palette.accent}
            strokeWidth="2"
            rx="2"
          />

          {/* Heated floor overlay */}
          {has("heated_floor") && (
            <rect
              x={x + 4}
              y={y + 4}
              width={displayW - 8}
              height={displayH - 8}
              fill="url(#heated-floor)"
              pointerEvents="none"
            />
          )}

          {/* Door swing — bottom-left corner */}
          <path
            d={`M ${x + 8} ${y + displayH} L ${x + 8} ${y + displayH - 36} A 36 36 0 0 0 ${x + 44} ${y + displayH}`}
            fill="none"
            stroke={palette.accent}
            strokeWidth="1"
            strokeDasharray="3 3"
            opacity="0.6"
          />
        </g>

        {/* Vanity — top-left */}
        <Vanity
          x={x + 12}
          y={y + 12}
          w={Math.min(displayW * 0.42, 180)}
          h={32}
          double={has("double_vanity")}
          palette={palette}
        />

        {/* Toilet — bottom of vanity wall */}
        <Toilet
          x={x + 18}
          y={y + displayH - 70}
          palette={palette}
          smart={has("smart_toilet")}
        />

        {/* Shower or tub — right side */}
        {has("walk_in_shower") ? (
          <WalkInShower
            x={x + displayW - 122}
            y={y + 12}
            w={110}
            h={displayH * 0.55}
            palette={palette}
            glass={has("frameless_glass")}
          />
        ) : has("freestanding_tub") ? (
          <FreestandingTub
            x={x + displayW - 110}
            y={y + 24}
            palette={palette}
          />
        ) : (
          <Tub x={x + displayW - 122} y={y + 12} w={110} palette={palette} />
        )}

        {/* Lighting indicators */}
        {has("new_lighting") && (
          <g>
            <LightDot cx={x + displayW * 0.25} cy={y + 6} palette={palette} />
            <LightDot cx={x + displayW * 0.5} cy={y + 6} palette={palette} />
            <LightDot cx={x + displayW * 0.75} cy={y + 6} palette={palette} />
          </g>
        )}

        {/* Exhaust fan */}
        {has("exhaust_fan") && (
          <g
            transform={`translate(${x + displayW * 0.5} ${y + displayH * 0.5})`}
            opacity="0.55"
          >
            <circle r="7" fill="none" stroke={palette.accent} strokeWidth="1" />
            <path
              d="M -4 0 A 4 4 0 0 1 4 0 M -2 -3 L -2 3 M 2 -3 L 2 3"
              stroke={palette.accent}
              strokeWidth="1"
              fill="none"
            />
          </g>
        )}

        {/* Dimensions */}
        <text
          x={cx}
          y={y + displayH + 22}
          textAnchor="middle"
          fontFamily="JetBrains Mono, monospace"
          fontSize="11"
          fill="#5C5F5A"
        >
          {widthFt}' × {heightFt}' · {sqft} sq ft
        </text>

        {/* Scope badge — top right */}
        <g transform="translate(490 22)">
          <rect
            width="100"
            height="22"
            rx="11"
            fill={palette.accent}
            opacity="0.92"
          />
          <text
            x="50"
            y="15"
            textAnchor="middle"
            fontFamily="Work Sans, sans-serif"
            fontSize="10"
            fontWeight="600"
            fill="#FFF"
            letterSpacing="0.4"
          >
            {TYPE_LABEL[remodel_type] || "Partial remodel"}
          </text>
        </g>

        {/* Material badge — top left */}
        <g transform="translate(20 22)">
          <rect
            width="100"
            height="22"
            rx="11"
            fill="#FFFFFF"
            stroke={palette.accent}
            strokeWidth="1"
          />
          <text
            x="50"
            y="15"
            textAnchor="middle"
            fontFamily="Work Sans, sans-serif"
            fontSize="10"
            fontWeight="600"
            fill={palette.accent}
            letterSpacing="0.4"
          >
            {MATERIAL_LABEL[material] || "Ceramic"}
          </text>
        </g>
      </svg>

      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="font-mono text-ink-faint">Live preview</span>
        <span className="font-mono text-ink-muted">
          {fixtures.length} fixture{fixtures.length === 1 ? "" : "s"} selected
        </span>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Fixture pieces
// ────────────────────────────────────────────────────────────────────────

const Vanity = ({ x, y, w, h, double, palette }) => (
  <g>
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      fill={palette.fixture}
      stroke={palette.fixtureStroke}
      strokeWidth="1.2"
      rx="3"
    />
    <text
      x={x + 4}
      y={y - 4}
      fontFamily="Work Sans, sans-serif"
      fontSize="8.5"
      fill={palette.accent}
      letterSpacing="0.3"
    >
      {double ? "DOUBLE VANITY" : "VANITY"}
    </text>
    {double ? (
      <>
        <circle
          cx={x + w * 0.28}
          cy={y + h / 2}
          r={9}
          fill="#fff"
          stroke={palette.fixtureStroke}
          strokeWidth="1"
        />
        <circle
          cx={x + w * 0.72}
          cy={y + h / 2}
          r={9}
          fill="#fff"
          stroke={palette.fixtureStroke}
          strokeWidth="1"
        />
      </>
    ) : (
      <circle
        cx={x + w / 2}
        cy={y + h / 2}
        r={9}
        fill="#fff"
        stroke={palette.fixtureStroke}
        strokeWidth="1"
      />
    )}
  </g>
);

const Toilet = ({ x, y, palette, smart }) => (
  <g>
    <rect
      x={x}
      y={y}
      width={28}
      height={18}
      fill={palette.fixture}
      stroke={palette.fixtureStroke}
      strokeWidth="1.2"
      rx="2"
    />
    <ellipse
      cx={x + 14}
      cy={y + 32}
      rx={14}
      ry={11}
      fill={palette.fixture}
      stroke={palette.fixtureStroke}
      strokeWidth="1.2"
    />
    {smart && (
      <circle
        cx={x + 14}
        cy={y + 9}
        r="2"
        fill={palette.accent}
        opacity="0.9"
      />
    )}
    <text
      x={x + 36}
      y={y + 26}
      fontFamily="Work Sans, sans-serif"
      fontSize="8.5"
      fill={palette.accent}
      letterSpacing="0.3"
    >
      {smart ? "SMART TOILET" : "TOILET"}
    </text>
  </g>
);

const Tub = ({ x, y, w, palette }) => (
  <g>
    <rect
      x={x}
      y={y}
      width={w}
      height={56}
      fill={palette.fixture}
      stroke={palette.fixtureStroke}
      strokeWidth="1.2"
      rx="6"
    />
    <rect
      x={x + 5}
      y={y + 5}
      width={w - 10}
      height={46}
      fill="none"
      stroke={palette.fixtureStroke}
      strokeWidth="0.8"
      rx="4"
      opacity="0.7"
    />
    <text
      x={x + 4}
      y={y - 4}
      fontFamily="Work Sans, sans-serif"
      fontSize="8.5"
      fill={palette.accent}
      letterSpacing="0.3"
    >
      TUB / SHOWER
    </text>
  </g>
);

const WalkInShower = ({ x, y, w, h, palette, glass }) => (
  <g>
    <rect
      x={x}
      y={y}
      width={w}
      height={h}
      fill={palette.fixture}
      stroke={palette.fixtureStroke}
      strokeWidth={glass ? "1.6" : "1.2"}
      rx="3"
      strokeDasharray={glass ? "0" : "0"}
    />
    {/* Tile pattern inside */}
    <pattern
      id="shower-tile"
      x={x}
      y={y}
      width="14"
      height="14"
      patternUnits="userSpaceOnUse"
    >
      <rect width="14" height="14" fill={palette.fixture} />
      <path
        d="M 14 0 L 0 0 0 14"
        stroke={palette.fixtureStroke}
        strokeWidth="0.4"
        fill="none"
        opacity="0.6"
      />
    </pattern>
    <rect
      x={x + 2}
      y={y + 2}
      width={w - 4}
      height={h - 4}
      fill="url(#shower-tile)"
    />
    {/* Drain */}
    <circle
      cx={x + w / 2}
      cy={y + h - 14}
      r="3"
      fill="none"
      stroke={palette.accent}
      strokeWidth="1"
    />
    <text
      x={x + 4}
      y={y - 4}
      fontFamily="Work Sans, sans-serif"
      fontSize="8.5"
      fill={palette.accent}
      letterSpacing="0.3"
    >
      WALK-IN SHOWER{glass ? " · GLASS" : ""}
    </text>
  </g>
);

const FreestandingTub = ({ x, y, palette }) => (
  <g>
    <ellipse
      cx={x + 50}
      cy={y + 40}
      rx={48}
      ry={28}
      fill={palette.fixture}
      stroke={palette.fixtureStroke}
      strokeWidth="1.4"
    />
    <ellipse
      cx={x + 50}
      cy={y + 40}
      rx={40}
      ry={22}
      fill="none"
      stroke={palette.fixtureStroke}
      strokeWidth="0.8"
      opacity="0.7"
    />
    <text
      x={x}
      y={y - 4}
      fontFamily="Work Sans, sans-serif"
      fontSize="8.5"
      fill={palette.accent}
      letterSpacing="0.3"
    >
      FREESTANDING TUB
    </text>
  </g>
);

const LightDot = ({ cx, cy, palette }) => (
  <g>
    <circle cx={cx} cy={cy} r="4" fill={palette.accent} opacity="0.18" />
    <circle cx={cx} cy={cy} r="1.5" fill={palette.accent} />
  </g>
);
